import {
  Box,
  Chip,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil, noop } from "lodash-es";
import { useMemo, useState } from "react";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { FeatureName } from "@/common/constants/featureName";
import { ArrayHelper } from "@/common/helpers/array";
import { RepairHelper } from "@/common/helpers/entity/repair";
import { RepairSparePartHelper } from "@/common/helpers/entity/repairSparePart";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { featureManager } from "@/config/features";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  RepairSparePartDto,
  RepairSparePartGetPaginatedDto,
} from "@/core/api/generated/v1";

import AppBadge from "../../Badge/AppBadge";
import AppIconButton from "../../Button/AppIconButton";
import AppTypography from "../../Text/AppTypography";
import {
  RepairSparePartAutocompleteCustomFilters,
  RepairSparePartAutocompleteCustomFiltersValues,
  computeCustomFilters,
} from "./RepairSparePartAutocompleteCustomFiltersInput";
import { RepairSparePartAutocompleteCustomFiltersInputPopover } from "./RepairSparePartAutocompleteCustomFiltersInputPopover";
import RepairSparePartCreateUpdateModal from "./RepairSparePartCreateUpdateModal";
import RepairSparePartCrossTenantSelectorPopover from "./RepairSparePartCrossTenantSelectorPopover";
import RepairSparePartDetalizationInline from "./RepairSparePartDetalizationInline";
import RepairSparePartViewModal from "./View/RepairSparePartViewModal";

const defaultDisplayProps = {
  viewInModal: true,
  editInModal: true,
  crossTenantSelector: true,
  customFilters: true,
};

export type RepairSparePartAutocompleteOption = BaseAutocompleteOption<RepairSparePartDto>;

export const getRepairSparePartOptionTitle = (data: RepairSparePartDto): string =>
  StringHelper.join([data.name || data.localNumber], ", ", {
    skipEmpty: true,
  });

export const repairSparePartToOption = (
  data: RepairSparePartDto,
): RepairSparePartAutocompleteOption => ({
  id: data.id!,
  title: getRepairSparePartOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface RepairSparePartAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<RepairSparePartDto> {
  searchFilters?: Partial<Omit<RepairSparePartGetPaginatedDto, "search" | "includeIds">>;
  displayProps?: Partial<typeof defaultDisplayProps>;
  /** Custom filters can be changed by user. */
  defaultCustomFilters?: RepairSparePartAutocompleteCustomFiltersValues;
}

export default function RepairSparePartAutocomplete({
  searchFilters,
  displayProps,
  defaultCustomFilters,

  isPreload = true,
  onLoaded,
  ...otherProps
}: RepairSparePartAutocompleteProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();
  const authorizationInfo = useAuthorizationInfo();

  const initialFiltersEnablement = useMemo(
    () => RepairSparePartHelper.getAutocompleteSettings()?.customFilters?.enablementMap,
    [],
  );
  const [customFilters, setCustomFilters] =
    useState<RepairSparePartAutocompleteCustomFilters | null>(() =>
      defaultCustomFilters
        ? computeCustomFilters({
            enablementMap: initialFiltersEnablement,
            values: defaultCustomFilters,
          })
        : null,
    );
  const [isCustomFiltersChanged, setIsCustomFiltersChanged] = useState(false);

  // set default filters (when changed) if local filters not set yet
  if (defaultCustomFilters && (!customFilters || !customFilters.isAny)) {
    const newCustomFilters = computeCustomFilters({
      enablementMap: initialFiltersEnablement,
      values: defaultCustomFilters,
    });
    if (newCustomFilters.isAny) {
      setCustomFilters(newCustomFilters);
    }
  }

  // console.log("RepairSparePartAutocomplete", {
  //   searchFilters,
  //   autocompleteSettings: RepairSparePartHelper.getAutocompleteSettings(),
  //   initialFiltersEnablement,
  //   defaultCustomFilters,
  //   customFilters,
  //   isCustomFiltersChanged,
  // });

  return (
    <BaseEntitySearchAutocomplete
      debugKey='RepairSparePartAutocomplete'
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={repairSparePartToOption}
      isPreload={isPreload}
      isIgnoreCanLoadCondition={isCustomFiltersChanged} // hack to ensure refetch on custom filters change
      onLoaded={(entities) => {
        setIsCustomFiltersChanged(false);
        onLoaded && onLoaded(entities);
      }}
      request={{
        requestFunc: apiClient.repairSparePartApi.apiV1RepairSparePartsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          repairSparePartGetPaginatedDto: {
            ...searchFilters,

            // prefer predefined filters over custom user filters
            partTypeId: searchFilters?.partTypeId || customFilters?.valuesEnabled?.partTypeId,
            vehicleSize: searchFilters?.vehicleSize || customFilters?.valuesEnabled?.vehicleSize,
            bodyType: searchFilters?.bodyType || customFilters?.valuesEnabled?.bodyType,
            fuelType: searchFilters?.fuelType || customFilters?.valuesEnabled?.fuelType,
            makeId: searchFilters?.makeId || customFilters?.valuesEnabled?.makeId,
            modelId: searchFilters?.modelId || customFilters?.valuesEnabled?.modelId,
            generationId: searchFilters?.generationId || customFilters?.valuesEnabled?.generationId,
            modificationId:
              searchFilters?.modificationId || customFilters?.valuesEnabled?.modificationId,
            ids: ArrayHelper.undefinedIfEmpty([
              ...(searchFilters?.ids || []),
              ...(customFilters?.valuesEnabled?.suggestedIds || []),
              ...(customFilters?.valuesEnabled?.restrictedIds || []),
            ]),
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          repairSparePartGetPaginatedDto: {
            ...params.repairSparePartGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters, customFilters],
      }}
      label='Repair spare part'
      placeholder='Search for repair spare part...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='repairSparePart' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant={
                    option.optionType === AutocompleteOptionType.DynamicCreateNew
                      ? "subtitle1"
                      : "body1"
                  }
                  component='div'
                >
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Box>{option.data?.name || option.title}</Box>

                    {!isNil(option.data?.matchMeta?.score) && (
                      <Box>
                        <Tooltip title='The bigger the match score the better the result suits your request.'>
                          <Typography variant='caption'>
                            <Chip
                              size='extraSmall'
                              variant='filled'
                              label={<>Match score: {option.data?.matchMeta?.score} / 100</>}
                            />
                          </Typography>
                        </Tooltip>
                      </Box>
                    )}
                  </Stack>
                </Typography>
              }
              secondary={
                option.data && (
                  <Box>
                    <AppTypography ellipsing={{ enabled: true }} component='div' variant='body2'>
                      {option.data?.partType?.name}
                    </AppTypography>

                    {option.data?.description && (
                      <AppTypography
                        tooltipProps={{
                          title: option.data?.description,
                          enterDelay: 500,
                          enterNextDelay: 500,
                        }}
                        ellipsing={{ enabled: true }}
                        component='div'
                        variant='body2'
                      >
                        {option.data?.description}
                      </AppTypography>
                    )}

                    {ArrayHelper.isNotEmpty(option.data.detalizations) && (
                      <AppTypography
                        tooltipProps={{
                          title: (
                            <Stack>
                              {option.data.detalizations.map((x, j) => (
                                <RepairSparePartDetalizationInline key={j} detalization={x} />
                              ))}
                            </Stack>
                          ),
                          enterDelay: 500,
                          enterNextDelay: 500,
                        }}
                        ellipsing={{ enabled: true }}
                        component='div'
                        variant='body2'
                      >
                        <Stack>
                          {option.data.detalizations.map((x, j) => (
                            <RepairSparePartDetalizationInline key={j} detalization={x} />
                          ))}
                        </Stack>
                      </AppTypography>
                    )}

                    {RepairHelper.isExternal({
                      tenantId: currentTenant?.id,
                      repairEntity: option.data,
                    }) && (
                      <Box>
                        <Chip
                          variant='outlined'
                          size='extraSmall'
                          label={
                            <>
                              <AppIcon of='external' inText /> External
                            </>
                          }
                        />
                      </Box>
                    )}
                  </Box>
                )
              }
            />
          </ListItem>
        );
      }}
      entityViewModal={{
        if:
          displayProps?.viewInModal &&
          authorizationInfo.hasPermissions([AppPermission.RepairCatalogRead]),
        renderModal: (params) => (
          <RepairSparePartViewModal
            {...params.dialogProps}
            viewProps={{
              repairSparePart: params.entity,
            }}
          />
        ),
      }}
      entityEditModal={{
        if:
          displayProps?.editInModal &&
          authorizationInfo.hasPermissions([AppPermission.RepairCatalogManage]),
        renderModal: (params) => (
          <RepairSparePartCreateUpdateModal
            {...params.dialogProps}
            createUpdateProps={{
              repairSparePartId: params.entity.id,
              repairSparePart: params.entity,
              onSave: (newValue) => {
                params.handleEntityUpdated(newValue);
                params.dialogProps?.onClose?.({}, "escapeKeyDown");
              },
            }}
          />
        ),
      }}
      endAdornmentActions={{
        actions: [
          {
            if:
              displayProps?.crossTenantSelector &&
              featureManager.isEnabled(FeatureName.CrossTenantRepairEntityAccess),
            renderAction: (params) => (
              <RepairSparePartCrossTenantSelectorPopover
                clickBehavior={{}}
                trigger={
                  <AppIconButton
                    {...params.iconButtonProps}
                    title='My cross-company repair spare parts'
                  >
                    <AppIcon of='crossTenantBrowse' />
                  </AppIconButton>
                }
                selectorProps={{
                  repairSparePartAutocompleteProps: {
                    searchFilters: { match: searchFilters?.match },
                  },
                  onSave: (newValue) => {
                    console.log("Cross-tenant repair spare part selected", newValue);
                    if (newValue?.repairEntity) {
                      params.handleEntitySelected(newValue?.repairEntity);
                    }
                  },
                  onCancel: () => {},
                }}
              />
            ),
          },
          {
            if: displayProps?.customFilters,
            renderAction: (params) => (
              <RepairSparePartAutocompleteCustomFiltersInputPopover
                clickBehavior={{}}
                trigger={
                  <AppIconButton {...params.iconButtonProps} color='default' title='Filters'>
                    <AppIcon of='filter' />
                    {customFilters?.isAnyEnabled && (
                      <AppBadge
                        sx={{ position: "absolute", top: 0, right: 0 }}
                        color='secondary'
                        overlap='circular'
                        badgeContent={customFilters?.countEnabled}
                      />
                    )}
                  </AppIconButton>
                }
                subProps={{
                  searchFilters: searchFilters,
                  defaultFilters: customFilters?.values,
                  defaultEnablementMap: customFilters?.enablementMap,
                  onSave: (newValue) => {
                    setIsCustomFiltersChanged(true);
                    setCustomFilters(newValue);
                    RepairSparePartHelper.saveAutocompleteCustomFilters(newValue);
                  },
                  onCancel: noop,
                }}
              />
            ),
          },
        ],
      }}
      {...otherProps}
    />
  );
}
